/**
 * Inline form validation helpers.
 *
 * Every helper returns EITHER a string (the human-readable error) OR
 * null / "" when the value is valid. That lets callers do:
 *
 *   const err = emailError(email);
 *   if (err) return <p>{err}</p>;
 *
 * and lets you disable submit buttons with:
 *
 *   const canSubmit = !emailError(email) && !amountError(amount);
 */

// ---------------------------------------------------------------------------
// Email
// ---------------------------------------------------------------------------
// Deliberately permissive — the backend does the strict check. We only
// need to catch obvious typos (missing @, missing TLD, spaces).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const isEmail = (v) => typeof v === "string" && EMAIL_RE.test(v.trim());

export const emailError = (v, { required = true } = {}) => {
  const s = (v || "").trim();
  if (!s) return required ? "Please enter your email." : "";
  if (s.length > 200) return "Email is too long.";
  if (!EMAIL_RE.test(s)) return "That doesn't look like a valid email address.";
  return "";
};

// ---------------------------------------------------------------------------
// Money / amounts (accepts CAD dollars as string or number)
// ---------------------------------------------------------------------------
export const amountError = (v, { required = true, max = 100_000, min = 0 } = {}) => {
  if (v === "" || v == null) return required ? "Please enter an amount." : "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "Enter a number.";
  if (n < min) return `Must be at least $${min}.`;
  if (n > max) return `Amount looks too high — max $${max.toLocaleString()}.`;
  return "";
};

// ---------------------------------------------------------------------------
// Required text
// ---------------------------------------------------------------------------
export const requiredError = (v, label = "This field") => {
  const s = (typeof v === "string" ? v : "").trim();
  if (!s) return `${label} is required.`;
  return "";
};

export const minLenError = (v, min, label = "This field") => {
  const s = (typeof v === "string" ? v : "").trim();
  if (!s) return `${label} is required.`;
  if (s.length < min) return `${label} must be at least ${min} characters.`;
  return "";
};

// ---------------------------------------------------------------------------
// Canadian phone (loose — 10 digits, ignoring formatting)
// ---------------------------------------------------------------------------
export const phoneError = (v, { required = false } = {}) => {
  const s = (v || "").replace(/\D+/g, "");
  if (!s) return required ? "Please enter a phone number." : "";
  if (s.length < 10) return "Enter a 10-digit Canadian phone number.";
  if (s.length > 11) return "That looks too long — 10 digits please.";
  return "";
};

// ---------------------------------------------------------------------------
// Positive integer
// ---------------------------------------------------------------------------
export const intError = (v, { required = true, min = 0, max = 100000, label = "Value" } = {}) => {
  if (v === "" || v == null) return required ? `${label} is required.` : "";
  const n = Number(v);
  if (!Number.isInteger(n)) return `${label} must be a whole number.`;
  if (n < min) return `${label} must be at least ${min}.`;
  if (n > max) return `${label} must be at most ${max}.`;
  return "";
};


// ---------------------------------------------------------------------------
// Text — empty guard + max length + code-character rejection.
// Added Feb 2026 for the app-wide safety-net batch. All 3 checks in
// one call so form handlers don't repeat themselves.
// ---------------------------------------------------------------------------
const ZERO_WIDTH_RE = /[\u200B\u200C\u200D\u2060\uFEFF]/g;
const SCRIPT_TAG_RE = /<\/?script\b[^>]*>/i;
const JS_URL_RE = /^\s*javascript:/i;

export function sanitizeText(s) {
  if (typeof s !== "string") return "";
  return s.replace(ZERO_WIDTH_RE, "").trim();
}

export function textError(v, { min = 1, max = 4000, allowEmpty = false, label = "This field" } = {}) {
  const s = sanitizeText(v);
  if (!s) return allowEmpty ? "" : `${label} is required.`;
  if (s.length < min) return `${label} must be at least ${min} characters.`;
  if (s.length > max) return `${label} is too long (${s.length}/${max}).`;
  if (SCRIPT_TAG_RE.test(s) || JS_URL_RE.test(s)) {
    return `${label} contains code characters that aren't allowed.`;
  }
  return "";
}

/**
 * Given a shape { fieldName: [value, validatorFn] } return
 * { ok, errors, firstError }. One-line guard for whole forms.
 */
export function validateForm(shape) {
  const errors = {};
  let firstError = "";
  for (const [name, [value, fn]] of Object.entries(shape)) {
    const msg = fn(value);
    if (msg) {
      errors[name] = msg;
      firstError = firstError || msg;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors, firstError };
}

